import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { Line } from "@vds/lines";
import { Loader } from "@vds/loaders";
import VectorIcon from "../../assets/icons/External_link.svg";
import BlueVectorIcon from "../../assets/icons/Blue_Vector.svg";
import CPNotification from "../../pages/Login/CPNotification";
import { setServiceDetails, setReschedule, getJobDetails } from "../../store/actions/schedulingportal/technicianAppointment";
import ServiceTotal from "./common/ServiceTotal";
import { CountryMapping } from "./RequestOverviewPage/AppointmentDetails";
import {
  CaseNumberTextLink,
  CPButton,
  CustomisedTertiaryButton,
  Img,
  StyledBlueAnchorTag,
  StyledImage,
  TextWrapper,
  TextWrapperSpan,
  Wrapper,
} from "../../constants/common/StyledGlobalConstants";
import RedesignedBreadCrumbs from "../common/RedesignedBreadCrumbs";
import Chip from "../cases/Chip";
import TechnicianAppointmentModal from "./TechnicianAppointmentsModal";
import AppointmentNotFoundIcon from "../../assets/images/Empty-list.png";
import SomethingWentWrongErrorIcon from "../../assets/images/Generic-error.png";
import "../../constants/common/StyleOnlyForScreenReader.css";
import { caseSubjectField } from "./common/JobsPayload";
import { DEVICE_BREAKPOINTS } from "../../constants/appConstants";
import { chatDisplayHandler } from "../../util/chatBotUtils";
import { SUPPORT_PHONE_NUMBERS } from "../../constants/technicianAppointmentConstants";
import { getPendoIdPrefix } from "../../util/cfstPendoUtils";

const { TABLET, DESKTOP } = DEVICE_BREAKPOINTS;

const BoxWrapper = styled(Wrapper)`
  border: 1px solid #d8dada;
  border-radius: 8px;
  padding: 24px;
`;

const TitleTextWrapper = styled(TextWrapper)`
  font-family: neue-haas-grotesk-text;
  font-weight: 700;
  font-size: 20px;
  line-height: 24px;
  grid-row: ${(props) => props.gridRow};
`;

const HeaderTextWrapper = styled(TextWrapper)`
  font-family: neue-haas-grotesk-text;
  font-weight: 700;
  font-size: ${(props) => (props.fontSize ? props.fontSize : "14px")};
  line-height: ${(props) => (props.lineHeight ? props.lineHeight : "20px")};
`;

export const BodyTextWrapper = styled(TextWrapper)`
  font-family: neue-haas-grotesk-text;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  text-align: ${(props) => props.textAlign};
`;

export const BodyTextWrapperSpan = styled(TextWrapperSpan)`
  font-family: neue-haas-grotesk-text;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  cursor: ${(props) => props.cursor};

  &:hover {
    cursor: ${(props) => props.isLink && "pointer"};
    text-decoration-thickness: ${(props) => props.isLink && "2px"};
  }
  &:focus {
    ${(props) =>
      props.isLink &&
      `
    border-radius: 2px;
    border: 2px solid; 
    outline: none;
  `}
  }
  &:active {
    ${(props) =>
      props.isLink &&
      `
    border: none;
    outline: none; 
    text-decoration-thickness: 0px;
  `}
  }
`;

export const StyledTextLink = styled(CaseNumberTextLink)`
  color: #006fc1 !important;
  text-decoration-line: underline;
  border-bottom: none;
  &:hover {
    text-decoration-thickness: 2px;
    border-bottom: none;
  }
  cursor: pointer;
`;

const CustomisedLine = styled(Line)`
  height: ${(props) => (props.height ? props.height : "1px")};
  background: ${(props) => (props.background ? props.background : "#d8dada")};
  margin: ${(props) => props.margin};
`;

const TryAgainButton = styled(CustomisedTertiaryButton)`
  & span {
    padding: 8px 16px;
  }
`;

const ViewAppointmentDetails = () => {
  const [isLoading, setIsLoading] = useState(false);
  const breadcrumbRef = useRef();
  const mainContentRef = useRef();
  const firstTabPressHandle = useRef(false);
  let { appointmentId } = useParams();
  const navigate = useNavigate();
  const segments = useLocation().pathname.split("/").filter(Boolean); // Split and filter out empty segments
  const currentPage = segments[segments.length - 2]; // Get the second-to-last segment
  const { windowSize, isSourcewellGovernment } = useSelector((state) => state.ACCOUNT);
  const { jobDetails, isReschedule, jobDetailsApiFailed, isStatus } = useSelector((state) => state.TECHNICIANAPPOINTMENT);
  const [breadcrumbsItemsList, setBreadcrumbsItemsList] = useState([
    {
      name: "Technician appointments",
      id: "cfst-appt-details-breadcrumb-back-link", // Default id
    },
    { name: `Appointment ${appointmentId}`, id: "appointment-number" },
  ]);
  const dispatch = useDispatch();

  useEffect(() => {
    chatDisplayHandler(sessionStorage.getItem("currentPath"));
    //Fetch job details
    dispatch(getJobDetails(appointmentId, setIsLoading));
    window.scrollTo(0, 0);
    const handleTabPress = (event) => {
      if (event.key == "Tab" && !firstTabPressHandle.current) {
        event.preventDefault();
        breadcrumbRef.current?.focus();
        firstTabPressHandle.current = true;
      }
    };
    document.addEventListener("keydown", handleTabPress);
    return () => {
      document.addEventListener("keydown", handleTabPress);
    };
  }, [appointmentId]);

  useEffect(() => {
    if (jobDetails?.id && jobDetails?.isAccountIdMatch && currentPage === "reschedule") {
      handleReschedule(true);
    }
  }, [jobDetails?.id]);

  const {
    status = "",
    transfer,
    removal,
    appointmentDateAndTime = "",
    address1 = "",
    address2 = "",
    city = "",
    state = "",
    zipCode = "",
    country = "",
    jobDuration = "",
    vendorName = "",
    caseNumber = "",
    caseId = "",
    createdBy = "",
    name = "",
    email = "",
    phone = "",
    notesToTechnician = "",
    customerChargeAmount = "",
    allServiceDetails = null,
    isAccountIdMatch = true,
    isRescheduleNotAllowed = true,
  } = jobDetails || {};

  useEffect(() => {
    if (!isAccountIdMatch || jobDetailsApiFailed) {
      let breadcrumbId;
      if (!isAccountIdMatch) {
        breadcrumbId = "cfst-appt-details-breadcrumb-back-link-on-appointment-not-found"; // ID for account mismatch
      } else if (jobDetailsApiFailed) {
        breadcrumbId = "cfst-appt-details-breadcrumb-back-link-on-api-failed"; // ID for API failure
      } else {
        breadcrumbId = "cfst-appt-details-breadcrumb-back-link"; // Default ID
      }
      setBreadcrumbsItemsList((prevList) => {
        const updatedList = [...prevList];
        updatedList[0] = {
          ...updatedList[0],
          id: breadcrumbId,
        };
        return updatedList;
      });
    }
  }, [isAccountIdMatch, appointmentId, jobDetailsApiFailed]);

  const renderServiceCountData = () => {
    const serviceData = caseSubjectField(transfer, removal);
    return renderData("Service", serviceData, windowSize.width < TABLET && 1);
  };

  const renderJobDuration = () => {
    const jobDurationInHours = jobDuration / 60;
    let duration = "";
    if (jobDurationInHours) {
      // Format to 1 decimal place and remove trailing zeros
      const formattedHours = parseFloat(jobDurationInHours.toFixed(1));
      duration = `Approximately ${formattedHours} ${formattedHours > 1 ? "hours" : "hour"}`;
    }
    return renderData("Duration", duration, windowSize.width < TABLET && 4);
  };

  const renderAppointmentDate = () => {
    return renderData("Appointment date and time", appointmentDateAndTime, windowSize.width < TABLET && 2);
  };

  const renderAppointmentLocation = () => {
    let address = "";
    let address2value = address2 ? `, ${address2}` : "";
    if (address1 && city && state && zipCode && country) {
      address = `${address1}${address2value}, ${city}, ${state} ${zipCode}, ${CountryMapping[country] || country}`;
    }
    return renderData("Appointment location", address, windowSize.width < TABLET && 3);
  };

  const renderData = (label, value, row) => (
    <Wrapper gap="2px" gridRow={row}>
      <HeaderTextWrapper>{label}</HeaderTextWrapper>
      {label !== "Case number" ? (
        <BodyTextWrapper aria-label={`label`}>{value}</BodyTextWrapper>
      ) : (
        <StyledBlueAnchorTag
          isLink
          aria-label={`${label}${value}`}
          tabIndex={label === "Case number" ? "0" : undefined}
          id={`${getPendoIdPrefix()}-appt-details-case-number`}
          onClick={() => navigate(`/support/cases/viewCaseDetails/${caseId}`)}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && navigate(`/support/cases/viewCaseDetails/${caseId}`)}
        >
          {value}
        </StyledBlueAnchorTag>
      )}
    </Wrapper>
  );

  const appointmentNotFound = () => (
    <Wrapper display="flex" flexDirection="column" padding="10rem 0rem" alignItems="center" width="100%" role="alert">
      <Img src={AppointmentNotFoundIcon} width="100px" height="100px" alt=""></Img>
      <TextWrapper fontFamily="Neue Haas Grotesk Text Std" fontSize="0.875rem" lineHeight="1.25rem" margin="2rem 0 0 0">
        {"Appointment not found."}
      </TextWrapper>
    </Wrapper>
  );

  const somethingWentWrongAndTryAgain = () => (
    <Wrapper display="flex" flexDirection="column" marginTop="5rem" alignItems="center" width="100%" role="alert">
      <Img src={SomethingWentWrongErrorIcon} width="100px" height="100px"></Img>
      <TextWrapper fontFamily="Neue Haas Grotesk Text Std" fontSize="0.875rem" fontWeight="700" lineHeight="1.25rem" margin="2rem 0 0 0">
        {"Something went wrong"}
      </TextWrapper>
      <Wrapper display="flex" flexDirection="column" gap="1rem" alignItems="center" marginTop="0.5rem">
        <TextWrapper fontSize="0.875rem" lineHeight="1.25rem">
          Something went wrong while loading this page.
        </TextWrapper>
        <Wrapper>
          <TryAgainButton
            id="cfst-appt-details-try-again"
            buttonHeight={"32px"}
            boxButton={true}
            ariaLabel="try again"
            onClick={handleTryAgain}
            fontSize="12px"
          >
            TRY AGAIN
          </TryAgainButton>
        </Wrapper>
      </Wrapper>
    </Wrapper>
  );

  const getRescheduleNotificationMessage = (isSourcewellGovernment) => {
    const phoneNumber = isSourcewellGovernment ? SUPPORT_PHONE_NUMBERS.GOVERNMENT : SUPPORT_PHONE_NUMBERS.COMMERCIAL;

    return <>You cannot reschedule to within three business days of the original appointment. Call Support on {phoneNumber} to reschedule.</>;
  };

  const showIsWithInThreeBusinessDaysNotification = () => (
    <CPNotification isOpen={true} notificationType="info" closeButton={false} height="auto">
      <Wrapper>
        <BodyTextWrapper>{getRescheduleNotificationMessage(isSourcewellGovernment)}</BodyTextWrapper>
      </Wrapper>
    </CPNotification>
  );

  const showRescheduleButton = () => {
    return (
      <Wrapper display="grid" justifyItems="end" gap={windowSize.width < TABLET ? "16px" : "8px"}>
        <CPButton
          secondary={true}
          boxButton={true}
          buttonWidth={windowSize.width < TABLET ? "100%" : "auto"}
          onClick={() => handleReschedule()}
          id={`${getPendoIdPrefix()}-appt-details-reschedule-appt-btn`}
        >
          RESCHEDULE APPOINTMENT
        </CPButton>
        <Wrapper display="flex">
          <BodyTextWrapper textAlign={windowSize.width < TABLET ? "start" : "end"}>
            To change or cancel your appointment, contact{" "}
            <StyledBlueAnchorTag
              isLink
              aria-label={"To change or cancel your appointment, contact Support"}
              id="cfst-appt-details-cancel-appt-support-link"
              onClick={() => window.open("https://reveal-help.verizonconnect.com/hc/articles/17190169888787")}
              onKeyDown={(e) =>
                (e.key === "Enter" || e.key === " ") && window.open("https://reveal-help.verizonconnect.com/hc/articles/17190169888787")
              }
              tabIndex={0}
            >
              Support
            </StyledBlueAnchorTag>
            {". "}
            <a href="https://reveal-help.verizonconnect.com/hc/articles/17190169888787" tabIndex={-1} aria-hidden="true" target="_blank">
              <StyledImage
                width="12px"
                height="12px"
                src={BlueVectorIcon}
                alt="link"
                id="cfst-appt-details-cancel-appt-support-icon"
                tabIndex={-1}
                aria-hidden="true"
              />
            </a>
          </BodyTextWrapper>
        </Wrapper>
      </Wrapper>
    );
  };

  const handleReschedule = (directLanding = false) => {
    dispatch(setReschedule(true));
    let services = allServiceDetails?.map((service) => {
      const { serviceType, existingVehicle, newVehicle, relatedServices } = service;
      const { vehicleName, revealCoreESN, vin, registrationNumber } = existingVehicle;
      const { vin: newVin } = newVehicle || {};
      const isVin = revealCoreESN && vin;
      return {
        serviceType: serviceType,
        vin: vin,
        newVehicle: newVin || "",
        selectedVinOrPA: {
          vehicleName: vehicleName,
          vehicleNumber: registrationNumber,
          esn: revealCoreESN,
          vin: vin,
          registrationNumber: registrationNumber,
          isVin: isVin,
          newVin: newVin || null,
          assetName: vehicleName,
          assetNumber: registrationNumber,
        },
        selectedCameraAndAccessories: relatedServices,
      };
    });

    dispatch(setServiceDetails(services));
    !directLanding && navigate(`/account/technicianAppointments/reschedule/${appointmentId}`);
  };

  const handleTryAgain = () => {
    dispatch(getJobDetails(appointmentId, setIsLoading));
  };

  const getGridRow = (serviceType) => {
    if (windowSize.width < TABLET) {
      return serviceType === "Removal" ? 2 : 3;
    }
    return 1;
  };

  const getSpacings = () => {
    if (isRescheduleNotAllowed) {
      return windowSize.width < DESKTOP ? "100%" : "50% 50%";
    } else {
      return windowSize.width < TABLET ? "100%" : "50% 50%";
    }
  };

  const showTransferOrRemovalDetails = () => (
    <Wrapper display="grid" gap="24px">
      {allServiceDetails?.map((service, index) => {
        const { serviceType, existingVehicle, newVehicle, relatedServices } = service;
        const { vehicleName, revealCoreESN, vin, registrationNumber } = existingVehicle;
        const { vehicleName: newVehicleName, vin: newVin } = newVehicle || {};
        const isVin = revealCoreESN && vin;
        return (
          <Wrapper display="grid" gap="16px" key={index + 1}>
            <TitleTextWrapper>{serviceType}</TitleTextWrapper>
            <Wrapper display="grid" gridTemplateColumns={windowSize.width < TABLET ? "100%" : "50% 50%"} gridRowGap="16px" alignItems="baseline">
              <Wrapper display="grid" gap="16px" gridRow={1}>
                <HeaderTextWrapper fontSize="16px" lineHeight="22px">
                  {isVin ? "Current vehicle details" : "Current powered asset details"}
                </HeaderTextWrapper>
                {
                  <>
                    {isVin ? (
                      <Wrapper>
                        <BodyTextWrapper>Vehicle name: {vehicleName}</BodyTextWrapper>
                        <BodyTextWrapper>ESN: {revealCoreESN}</BodyTextWrapper>
                        <BodyTextWrapper>VIN: {vin}</BodyTextWrapper>
                        <BodyTextWrapper>Registration number: {registrationNumber}</BodyTextWrapper>
                      </Wrapper>
                    ) : (
                      <Wrapper>
                        <BodyTextWrapper>Asset name: {vehicleName}</BodyTextWrapper>
                        <BodyTextWrapper>Asset number: {registrationNumber}</BodyTextWrapper>
                      </Wrapper>
                    )}
                  </>
                }
              </Wrapper>
              {isVin && relatedServices && relatedServices !== "No accessories connected" && (
                <Wrapper display="grid" gap="16px" gridRow={getGridRow(serviceType)}>
                  <h3 className="sr-only" tabIndex="-1">
                    Cameras and other accessories
                  </h3>
                  <HeaderTextWrapper fontSize="16px" lineHeight="22px" aria-hidden="true" tabIndex="-1">
                    Cameras and other accessories
                  </HeaderTextWrapper>
                  <Wrapper>
                    {relatedServices.split(",").map((data, index) => (
                      <BodyTextWrapper key={index + 1}>{data}</BodyTextWrapper>
                    ))}
                  </Wrapper>
                </Wrapper>
              )}
              {serviceType === "Transfer" && (
                <Wrapper display="grid" gap="16px" gridRow={windowSize.width < TABLET ? 2 : 3}>
                  <HeaderTextWrapper fontSize="16px" lineHeight="22px">
                    {isVin ? "New vehicle details" : "New powered asset details"}
                  </HeaderTextWrapper>
                  {
                    <>
                      {isVin ? (
                        <Wrapper>
                          <BodyTextWrapper>Vehicle name: {newVehicleName}</BodyTextWrapper>
                          <BodyTextWrapper>VIN: {newVin}</BodyTextWrapper>
                        </Wrapper>
                      ) : (
                        <Wrapper>
                          <BodyTextWrapper>Asset name: {newVehicleName}</BodyTextWrapper>
                        </Wrapper>
                      )}
                    </>
                  }
                </Wrapper>
              )}
            </Wrapper>
            {serviceType === "Removal" && (
              <CPNotification isOpen={true} notificationType="warning" closeButton={false} height="auto">
                <Wrapper display="flex" gap="4px">
                  <BodyTextWrapper>
                    You will continue to be billed for this device after removal unless you update your contract. For more information, read the{" "}
                    <BodyTextWrapperSpan
                      isLink
                      textDecorationLine="underline"
                      cursor="pointer"
                      tabIndex={0}
                      aria-label="Contracts and cancellations FAQ link"
                      id="cfst-appt-details-contract-article-link"
                      onClick={() => window.open("https://reveal-help.verizonconnect.com/hc/articles/17190169888787")}
                    >
                      Contracts and cancellations FAQ
                    </BodyTextWrapperSpan>
                    {". "}
                    <a href="https://reveal-help.verizonconnect.com/hc/articles/17190169888787" target="_blank" aria-hidden="true" tabIndex={-1}>
                      <StyledImage width="12px" height="12px" src={VectorIcon} alt="link" />
                    </a>
                  </BodyTextWrapper>
                </Wrapper>
              </CPNotification>
            )}
          </Wrapper>
        );
      })}
    </Wrapper>
  );

  const renderAppointmentDetails = () => (
    <BoxWrapper>
      <Wrapper display="grid" gap="24px">
        <Wrapper display="grid" gridTemplateColumns={windowSize.width < TABLET ? "100%" : "1fr 12%"}>
          <TitleTextWrapper gridRow={windowSize.width < TABLET && 2} aria-label="Appointment details">
            Appointment details
          </TitleTextWrapper>
          <Wrapper justifyItems="end">
            <Chip status={status} cfst="true">
              {status}
            </Chip>
          </Wrapper>
        </Wrapper>
        <Wrapper display="grid" gridTemplateColumns={windowSize.width < TABLET ? "100%" : "50% 50%"} gridRowGap="16px" gridColumnGap="1rem">
          {renderServiceCountData()}
          {renderJobDuration()}
          {renderAppointmentDate()}
          {renderData("Technician", vendorName, windowSize.width < TABLET && 5)}
          {renderAppointmentLocation()}
          {renderData("Case number", caseNumber, windowSize.width < TABLET && 6)}
        </Wrapper>
      </Wrapper>
      <CustomisedLine margin="24px 0 16px 0"></CustomisedLine>
      <Wrapper
        display="grid"
        gridTemplateColumns={getSpacings}
        gridRowGap={isRescheduleNotAllowed ? windowSize.width < DESKTOP && "16px" : windowSize.width < TABLET && "16px"}
      >
        <Wrapper display="flex">
          <BodyTextWrapper color="#747474">
            Created by: <BodyTextWrapperSpan>{createdBy}</BodyTextWrapperSpan>
          </BodyTextWrapper>
        </Wrapper>
        {/* The Reschedule button should not be displayed, three business working days within the original appointment time. */}
        {/* Show notification to contact for support */}
        {status !== "Complete" && status !== "Cancelled" && isStatus !== "PO Released-All" && (
          <>{isRescheduleNotAllowed ? showIsWithInThreeBusinessDaysNotification() : showRescheduleButton()}</>
        )}
      </Wrapper>
    </BoxWrapper>
  );

  return (
    <>
      {isLoading ? (
        <Loader active={isLoading} fullscreen={true} />
      ) : (
        <>
          <Wrapper ref={mainContentRef} tabIndex="-1">
            <Wrapper>
              <RedesignedBreadCrumbs
                Items={breadcrumbsItemsList}
                ref={breadcrumbRef}
                handleBack={() => navigate(`/account/technicianAppointments`)}
              ></RedesignedBreadCrumbs>
            </Wrapper>

            {/* Display something went wrong and try again when job details failed */}
            {/* Display appointment not found screen when logged in use accountId and job accountId doesn't match */}
            {/* If view job api failed , display something went wrong and try again */}
            {/* If view job api success but accountId doesn't match , Appointment not found */}
            {jobDetailsApiFailed && (
              <>
                {/* <SomethingWentWrongTryAgain handleTryAgain={handleTryAgain} /> */}
                {somethingWentWrongAndTryAgain()}
              </>
            )}
            {jobDetails?.isAccountIdMatch === false ? (
              <>{appointmentNotFound()}</>
            ) : (
              !jobDetailsApiFailed && (
                <>
                  <Wrapper marginTop="2.5rem" marginBottom="2rem">
                    <TextWrapper aria-label="Appointment" fontFamily="neue-haas-grotesk-display" fontSize="2.5rem" fontWeight="700" lineHeight="3rem">
                      Appointment {appointmentId}
                    </TextWrapper>
                  </Wrapper>
                  {/* Appointment details start */}
                  {renderAppointmentDetails()}
                  {/* Appointment details end */}
                  {/* Point of contact for technician details start */}
                  <BoxWrapper marginTop="24px">
                    <Wrapper display="grid" gap="24px">
                      <TitleTextWrapper>Point of contact for technician</TitleTextWrapper>
                      <Wrapper display="grid" gridTemplateColumns={windowSize.width < TABLET ? "100%" : "50% 50%"} gridRowGap="16px">
                        {renderData("Contact", name, windowSize.width < TABLET && 1)}
                        {renderData("Phone number", phone ? "+1 " + phone : null, windowSize.width < TABLET && 3)}
                        {renderData("Email address", email, windowSize.width < TABLET && 2)}
                      </Wrapper>
                      {status && status !== "Completed" && status !== "Cancelled" && notesToTechnician && (
                        <>
                          <CustomisedLine />
                          <Wrapper display="grid" gap="8px">
                            <HeaderTextWrapper>Additional instructions for technician</HeaderTextWrapper>
                            <BodyTextWrapper>{notesToTechnician}</BodyTextWrapper>
                          </Wrapper>
                        </>
                      )}
                    </Wrapper>
                  </BoxWrapper>
                  {/* Point of contact for technician details end */}
                  {/* Service summary start */}
                  <TextWrapper fontSize="24px" fontWeight="700" lineHeight="32px" margin="40px 0 24px 0" aria-label="Service summary">
                    Service summary
                  </TextWrapper>
                  <BoxWrapper>
                    {/* Transfer or Removal details start */}
                    {showTransferOrRemovalDetails()}
                    {/* Transfer or Removal details end */}
                    <ServiceTotal fees={customerChargeAmount} />
                  </BoxWrapper>
                  {/* Service summary end */}
                </>
              )
            )}
          </Wrapper>
          {/* reschedule modal will be enable when user clicks on Reschedule Appointment Button */}
          {isReschedule && <TechnicianAppointmentModal serviceType={0} isOpen={isReschedule} setIsOpen={setReschedule} />}
        </>
      )}
    </>
  );
};

export default ViewAppointmentDetails;
